import { OrderPriceNegotiationStatus, OrderStatus } from '@/interface/order';
import type { CreatorCollaborationPackage } from './model';
import { getCreatorRateHistory } from './negotiationHistory';

export type CreatorPackageDealPriceState =
  | 'initial'
  | 'offer'
  | 'review'
  | 'awaiting_brand'
  | 'confirmed'
  | 'details';

const CONFIRMED_STATUSES = new Set<string>([
  OrderStatus.PendingProductionKickoff,
  OrderStatus.PendingCreatorAddress,
  OrderStatus.PendingCreatorAccountInfo,
  OrderStatus.PendingAccountSetup,
  OrderStatus.PendingProductShipment,
  OrderStatus.PendingProductArrival,
  OrderStatus.UnderProduction,
  OrderStatus.UnderReview,
  OrderStatus.ReviewRejected,
  OrderStatus.UnderRevision,
  OrderStatus.PendingPublish,
  OrderStatus.PublishRejected,
  OrderStatus.PendingPayment,
  OrderStatus.PaymentSubmitted,
  OrderStatus.PendingWithdraw,
  OrderStatus.PaymentFailed,
  OrderStatus.Paid,
  OrderStatus.Completed
]);

const CREATOR_REPEAT_COUNTER_STATUSES = new Set<string>([
  OrderPriceNegotiationStatus.UnderAdsFirstReview,
  OrderPriceNegotiationStatus.UnderAdsReview
]);

export function canCreatorRepeatPackageCounter(order: Record<string, any>) {
  return (
    order?.status === OrderStatus.CreatorApplied &&
    CREATOR_REPEAT_COUNTER_STATUSES.has(order?.priceNegotiationStatus)
  );
}

export function hasCreatorPackageNegotiationHistory(
  order: Record<string, any>
) {
  // Match the dialog's display records; raw ahaPicksOffer.history is not a list.
  return getCreatorRateHistory(order).length > 0;
}

export function getCreatorPackageBrandMessage(
  order: Record<string, any>
): string {
  if (
    order.status !== OrderStatus.CreatorApplied ||
    order.priceNegotiationStatus !==
      OrderPriceNegotiationStatus.UnderCreatorReview
  ) {
    return '';
  }

  // V2 history is chronological. Never fall back to an earlier brand offer:
  // it may have a different price, or the latest offer may have no message.
  const history = Array.isArray(order.negotiationHistory)
    ? order.negotiationHistory
    : [];
  const record = history.length
    ? history[history.length - 1]
    : order.priceNegotiationField;
  const source = record?.submitedBy ?? record?.submittedBy;
  if (history.length ? source !== 'ads' : source && source !== 'ads') {
    return '';
  }
  const message = record?.message ?? record?.messageToInfluencer;
  return typeof message === 'string' ? message.trim() : '';
}

export function getCreatorPackageDealPriceState(
  collaborationPackage: CreatorCollaborationPackage | null
): CreatorPackageDealPriceState | null {
  if (!collaborationPackage) return null;
  const order = collaborationPackage.raw;
  // Fulfilment is authoritative even if a stale negotiation status remains.
  if (CONFIRMED_STATUSES.has(order.status)) return 'confirmed';
  if (order.status === OrderStatus.Invited) {
    return 'initial';
  }
  if (
    order.status === OrderStatus.CreatorApplied &&
    order.priceNegotiationStatus ===
      OrderPriceNegotiationStatus.UnderCreatorReview
  ) {
    const history = Array.isArray(order.negotiationHistory)
      ? order.negotiationHistory
      : [];
    // A first brand offer may have one history entry; a creator quote or a
    // subsequent revision distinguishes the negotiation variant.
    return history.length > 1 ||
      history.some(
        (record: Record<string, any>) =>
          (record.submitedBy ?? record.submittedBy) === 'creator'
      )
      ? 'review'
      : 'offer';
  }
  if (
    order.status === OrderStatus.CreatorApplied &&
    [
      OrderPriceNegotiationStatus.UnderAdsFirstReview,
      OrderPriceNegotiationStatus.UnderAdsReview,
      OrderPriceNegotiationStatus.PendingAdsApproval
    ].includes(order.priceNegotiationStatus)
  ) {
    // TODO(Design): 1446:4207 has no waiting-brand detail-price variant.
    // Preserve the existing creator-quote layout and action permissions.
    return 'awaiting_brand';
  }
  return 'details';
}

/** Keep the initial quote button and invited notice on the same label. */
export function isCreatorMultiChannelInitialQuote(
  collaborationPackage: CreatorCollaborationPackage | null
): boolean {
  return (
    getCreatorPackageDealPriceState(collaborationPackage) === 'initial' &&
    (collaborationPackage?.placements.filter(
      (placement) => placement.raw?.active !== false
    ).length ?? 0) > 1
  );
}

// Use the existing package/channel price fields. Do not sum per-channel bonus
// values or invent the design's $20 when the API has not supplied a bonus.
export function getCreatorPackageInitialBonusAmount(
  collaborationPackage: CreatorCollaborationPackage
) {
  const order = collaborationPackage.raw;
  const value =
    order.priceFields?.[0]?.bonusAmount ??
    order.interestedPriceFields?.[0]?.bonusAmount ??
    collaborationPackage.placements
      .filter((placement) => placement.raw.active !== false)
      .map(
        (placement) =>
          placement.raw.priceFields?.[0]?.bonusAmount ??
          placement.raw.interestedPriceFields?.[0]?.bonusAmount
      )
      .find((amount) => amount !== undefined && amount !== null);
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
}
